import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { designShortId } from '../utils/designDisambiguation';

interface SwitchDesign {
  name: string;
  pluginId?: string;
}

export interface PendingDesignSwitch<T extends SwitchDesign> {
  /** Undefined when the step's live design has no card in the picker. */
  current?: SwitchDesign;
  next: T;
}

interface DesignSwitchConfirmDialogProps<T extends SwitchDesign> {
  open: boolean;
  pending: PendingDesignSwitch<T> | null;
  onConfirm: (design: T) => void;
  onClose: () => void;
}

const designLabel = (design: SwitchDesign) => {
  const shortId = designShortId(design.pluginId);
  return shortId ? `${design.name} (${shortId})` : design.name;
};

export function DesignSwitchConfirmDialog<T extends SwitchDesign>({
  open,
  pending,
  onConfirm,
  onClose,
}: DesignSwitchConfirmDialogProps<T>) {
  const { t } = useTranslation('storefront');

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <AlertDialogContent data-testid="design-switch-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('designPicker.confirmSwitch.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {pending && t('designPicker.confirmSwitch.description', {
              current: pending.current ? designLabel(pending.current) : t('designPicker.confirmSwitch.currentFallback'),
              next: designLabel(pending.next),
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('designPicker.confirmSwitch.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={() => pending && onConfirm(pending.next)}>
            {t('designPicker.confirmSwitch.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
